import * as Sentry from "@sentry/react";
import cx from "classnames";
import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Icon from "../../common/components/Icon/Icon";
import Tooltip from "../../common/components/Tooltips/Tooltip";
import {
    addExistingPolicyOwner,
    getApplicationPolicyOwners,
    removePolicyOwner,
    setCurrentPageProgress
} from "../api/ApiAppForm";
import AFLayout from "../components/AFLayout";
import AFNavigation from "../components/AFNavigation";
import AFSecure from "../components/AFSecure";
import { IViewContext, ViewContext } from "../providers/ViewStateProvider";
import AFAddPolicyOwnerDialog from "../components/Modals/AFAddPolicyOwnerDialog";
import { PageType } from "../enums/PageType";
import { useAFForm } from "../hooks/useAFForm";
import { IApiAppPolicyOwner } from "../interfaces/IApiAppFormData";
import { IPageInfo } from "../interfaces/IAppFormData";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import styles from "../scss/AFPolicyOwners.module.scss";

interface IAFPolicyOwnershipProps {
    applicationId: string;
}

const tooltipContent = `<strong class="bold">Couples</strong></br><span>Usually couples will have a "joint" policy, which means that they are both owners.</span></br></br><strong class="bold">Single People</strong></br><span>Usually the policy is owned by the person being insured. If someone in particular is to receive the payment, they might be an owner too.</span></br></br><strong class="bold">Can a child be an owner?</strong></br><span class="last">The minimum age is 16. If you wish to leave money to children, this can be done in your Will.</span>`;

const AFPolicyOwnership: React.FC = () => {
    const history = useHistory();
    const params = useParams<IAFPolicyOwnershipProps>();
    const applicationId = params.applicationId;
    const { setError } = useContext<IViewContext>(ViewContext);

    const [isReady, setIsReady] = useState(false);
    const [ownerList, setOwnerList] = useState<Array<IApiAppPolicyOwner>>([]);

    const [isOpenAddDialog, setIsOpenAddDialog] = useState(false);
    const { selectors, actions } = useContext(AppFormControllerContext);
    const {
        getCurrentPage,
        checkValidApplication
    } = useAFForm({ pageType: PageType.PolicyOwnership, currentClientNumber: 0 });

    const handleNextPage = (): void => {
        if (!ownerList?.length) return;
        const pageInfo: IPageInfo = {
            pageType: PageType.Payment,
            currentClientNumber: 1
        };
        setCurrentPageProgress(
            selectors.getApplicationId(),
            selectors.getQuoteGroupId(),
            pageInfo
        );
        history.push(`/app-form/submit/${applicationId}/payment`);
    };

    const handleBack = (): void => {
        setIsReady(false);
        const applicantList = selectors.getApplicantList();
        const lastApplicant = applicantList.reduce((prev, current) => (prev && prev.clientNumber > current.clientNumber) ? prev : current);
        history.push(`/app-form/details/${lastApplicant.applicantToken}/declarations`);
    };

    const handleGetList = (): void => {
        getApplicationPolicyOwners(applicationId, selectors.getQuoteGroupId())
            .then((list) => {
                if (!list || !list?.length) return;
                const policyOwnerList = list?.map((item) => ({
                    ...item,
                    active: true
                }));
                setOwnerList(policyOwnerList);
            })
            .catch(() => setOwnerList([]));
    };

    const handleRemovePolicyOwner = (policyOwner): void => {
        removePolicyOwner(
            Number(applicationId),
            selectors.getQuoteGroupId(),
            policyOwner?.appPolicyOwnerId
        )
            .then()
            .catch(() => {
                setError("There was a problem removing the policy owner.");
                if (typeof Sentry !== 'undefined') {
                    Sentry.captureMessage(`Error occured for ApplicationId ${applicationId} on the Policy Ownership page. There was a problem removing the policy owner.`, "error");
                }
            })
            .finally(() => handleGetList());
    };

    const addExistingOwner = (applicantId: number): void => {
        addExistingPolicyOwner(
            parseInt(applicationId),
            selectors.getQuoteGroupId(),
            applicantId
        )
            .then()
            .catch(() => {
                setError("There was a problem adding the policy owner.");
                if (typeof Sentry !== 'undefined') {
                    Sentry.captureMessage(`Error occured for ApplicationId ${applicationId} on the Policy Ownership page. There was a problem adding the policy owner.`, "error");
                }
            })
            .finally(() => handleGetList());
    };

    const handleInitialList = (): void => {
        setIsReady(true);
        getApplicationPolicyOwners(applicationId, selectors.getQuoteGroupId()).then(
            (res) => {
                if (!res || !res?.length) return;
                const policyOwnerList = res?.map((item) => ({
                    ...item,
                    active: true
                }));
                setOwnerList(policyOwnerList);
            }
        );
    };

    const getInactiveApplicant = (): Array<IApiAppPolicyOwner> => {
        const adultApplicantList = selectors.getApplicantList().filter((a) => !a.isChild);
        const inactiveApplicants = adultApplicantList.reduce((acc, item) => {
            const policyOwner = ownerList.find(
                (i) => i?.applicantId === item?.applicantId
            );
            if (!policyOwner) {
                return [...acc, item];
            }
            return acc;
        }, []);
        return inactiveApplicants;
    };

    useEffect(() => {
        if (isReady) return;

        checkValidApplication(applicationId, selectors);
        handleInitialList();
    }, [isReady]);

    return (
        <AFLayout currentPage={getCurrentPage()}>
            <AFAddPolicyOwnerDialog
                isOpen={isOpenAddDialog}
                applicationId={applicationId}
                onClose={(isContinue: boolean) => {
                    if (isContinue) {
                        handleGetList();
                    }
                    setIsOpenAddDialog(false);
                }}
            />
            <div className={cx(styles["layout"])}>
                <div
                    className={cx(
                        styles["af-ownership-container"],
                        styles["layout-flex-column--centered"]
                    )}>
                    <h2 className={cx(styles["ownership-title-label"])}>
                        Policy ownership
                    </h2>

                    <div className={styles["af-ownership"]}>
                        <h3 className={styles["af-ownership-label"]}>
                            This policy will be owned by:
                        </h3>

                        <div className={styles["af-owners-container"]}>
                            {ownerList?.map((item, idx) => (
                                <div
                                    className={styles["af-owner-item"]}
                                    key={`active${idx}`}>
                                    <Icon
                                        alt={`${item.firstName}-check-icon`}
                                        className={styles["af-owner-icon"]}
                                        size="l"
                                        id="GreenTickNew"
                                    />
                                    <h2 className={styles["af-owner-name"]}>
                                        {item.firstName}
                                    </h2>
                                    {ownerList?.length > 1 && < button
                                        className={styles["af-owner-btn"]}
                                        onClick={() => handleRemovePolicyOwner(item)}>
                                        <span>Remove</span>
                                    </button>}
                                </div>
                            ))}
                            {getInactiveApplicant()?.map((item, idx) => (
                                <button
                                    key={`inactive${idx}`}
                                    className={styles["af-owner-action"]}
                                    onClick={() => addExistingOwner(item?.applicantId)}>
                                    <Icon
                                        alt={`${item.firstName}-add-icon`}
                                        className={cx(styles["action-icon"])}
                                        size="ml"
                                        id="AccordionPlus"
                                    />
                                    <a>{item.firstName}</a>
                                </button>
                            ))}
                            <button
                                className={styles["af-owner-action"]}
                                onClick={() => setIsOpenAddDialog(true)}>
                                <Icon
                                    alt="add-icon"
                                    className={cx(styles["action-icon"])}
                                    size="ml"
                                    id="AccordionPlus"
                                />
                                <a>Add a new policy owner</a>
                            </button>
                        </div>

                        <div className={styles["af-ownership-policy"]}>
                            <div className={styles["policy-heading"]}>
                                <Icon
                                    alt="alert-icon"
                                    className={styles["af-policy-icon"]}
                                    size="xl"
                                    id="AlertInfo"
                                />
                                <h4>
                                    About policy ownership
                                </h4>
                            </div>
                            <div className={styles["af-policy-info"]}>
                                If a claim is made, your life insurance sum will be paid
                                to the Policy Owner(s). Policy owners must be aged 18
                                years or over. By default your policy will be owned by the
                                people insured. This is the most commonly chosen approach,
                                however you can change it if you want to.
                            </div>
                            <Tooltip
                                placement="top"
                                className={cx(styles["policy-link"], "btn-link")}
                                size="none"
                                tooltipContent={
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: tooltipContent
                                        }}
                                    />
                                }>
                                What do most people do?
                            </Tooltip>
                        </div>
                        <AFNavigation
                            handleBack={handleBack}
                            skipIsVisible={false}
                            forwardIsVisible={true}
                            forwardIsDisabled={!ownerList?.length}
                            forwardType="button"
                            onClick={handleNextPage}
                            forwardText={"Next"}
                        />
                    </div>
                </div>
            </div>
        </AFLayout>
    );
};

export default AFPolicyOwnership;
